import { z } from "zod";

/**
 * The agent's people tool (openspec change `agent-turn`, spec `find-who-knows`, D22, D23):
 * internal (no provider, no egress), offered in project turns to users whose effective role is
 * `reader` or above, never to guests. It names the people who appear on pages, documents and
 * tasks the asker can read now that match a topic, as the member list shows them to that viewer
 * (display names, never an e-mail). It writes nothing.
 */
export const FIND_PEOPLE_TOOL_NAME = "find_people";

export const AGENT_PEOPLE_LIMITS = {
  /** The topic as the model writes it (D22). */
  topic: 200,
  /** People returned at most. */
  people: 5,
  /** Signals per person at most. */
  signals: 3,
  /** Latest versions of a page whose authors and publishers count. */
  pageVersions: 5,
} as const;

/** `find_people` input (D22). */
export const findPeopleInputSchema = z.object({
  topic: z
    .string()
    .trim()
    .min(1)
    .max(AGENT_PEOPLE_LIMITS.topic)
    .describe("What the user wants someone for, in plain words"),
});
export type FindPeopleInput = z.infer<typeof findPeopleInputSchema>;

/**
 * Why a person is named (D22), with its score: a page's owner or verifier 5, an author or
 * publisher of one of its latest versions 3, a document's uploader 2, a task's assignee 2, its
 * creator 1. Comments and memory authorship are never signals.
 */
export const PEOPLE_SIGNAL_KINDS = [
  "page_owner",
  "page_verifier",
  "page_author",
  "page_publisher",
  "document_uploader",
  "task_assignee",
  "task_creator",
] as const;
export type PeopleSignalKind = (typeof PEOPLE_SIGNAL_KINDS)[number];

export const PEOPLE_SIGNAL_SCORES: Readonly<Record<PeopleSignalKind, number>> = {
  page_owner: 5,
  page_verifier: 5,
  page_author: 3,
  page_publisher: 3,
  document_uploader: 2,
  task_assignee: 2,
  task_creator: 1,
};

/** One person `find_people` returns: a name, why (≤ 3 signals) and their project role. */
export interface FoundPerson {
  name: string;
  signals: { kind: PeopleSignalKind; title: string; source?: string }[];
  role?: string;
}

/** `find_people` output: people, or an error the model reads. Never a count of hidden objects. */
export type FindPeopleOutput =
  | { people: FoundPerson[]; note: string }
  | { people?: undefined; error: string };
